import type { DocType } from './types';

/**
 * 양식 문서의 칸 정의(V574 · DOC-136). 정의 하나가 화면(DocSheet) · 저장 값(form_data) · PDF 를 함께 정한다.
 *
 * <p>form_data 는 칸 이름까지 실은 자기 서술형이다 — 서버 PDF(SafeJournalDocService.docPaper)가 종류를 몰라도 그리고,
 * 나중에 칸 정의가 바뀌어도 지난 문서는 적힌 그대로 남는다. 다시 열면 key 로 값을 찾아 지금 정의에 얹는다.
 */

/** 칸 하나. kind 기본은 text. select 는 options 중 하나, check 는 'Y' / ''. */
export type DocInput = {
  key: string;
  label: string;
  kind?: 'text' | 'textarea' | 'date' | 'time' | 'number' | 'select' | 'check';
  options?: string[];
  /** 한 줄을 다 쓴다(fields 칸). */
  wide?: boolean;
  /** textarea 줄 수. */
  rows?: number;
  /** 새 문서의 처음 값. */
  initial?: string;
};

export type DocTableColumn = DocInput & {
  /** 표 칸 폭(예: '12%' · '96px'). 비우면 남는 폭. */
  width?: string;
};

export type DocSection =
  | { kind: 'fields'; label?: string; fields: DocInput[] }
  | {
      kind: 'table';
      key: string;
      label: string;
      columns: DocTableColumn[];
      /** 새 문서가 처음 가질 줄 — 점검표처럼 항목이 정해진 표. 비우면 빈 줄 하나. */
      initialRows?: Array<Record<string, string>>;
    };

export type DocSchema = {
  code: DocType;
  /** 목록 · 할일에 보일 한 줄 제목을 이 칸 값으로(일지의 title). 비우면 제목 없음. */
  titleKey?: string;
  sections: DocSection[];
};

/** 저장되는 꼴 — 서버 docPaper 와 짝. */
export type FormData = {
  sections: Array<
    | { type: 'fields'; label?: string; items: Array<{ key: string; label: string; value: string }> }
    | { type: 'table'; key: string; label: string; columns: Array<{ key: string; label: string }>; rows: Array<Record<string, string>> }
  >;
};

export type DocValues = { values: Record<string, string>; tables: Record<string, Array<Record<string, string>>> };

/** 저장된 값 → 편집 값. 없으면 정의의 처음 값. */
export function readValues(schema: DocSchema, data: FormData | null | undefined): DocValues {
  const values: Record<string, string> = {};
  const tables: Record<string, Array<Record<string, string>>> = {};
  for (const section of schema.sections) {
    if (section.kind === 'fields') {
      for (const field of section.fields) values[field.key] = field.initial ?? '';
    } else {
      tables[section.key] = section.initialRows ? section.initialRows.map((row) => ({ ...row })) : [{}];
    }
  }
  for (const section of data?.sections ?? []) {
    if (section.type === 'fields') {
      for (const item of section.items) values[item.key] = item.value;
    } else {
      tables[section.key] = section.rows.map((row) => ({ ...row }));
    }
  }
  return { values, tables };
}

const emptyRow = (row: Record<string, string>) => Object.values(row).every((value) => !value || !value.trim());

/** 편집 값 → 저장 꼴. 표의 빈 줄은 버린다. */
export function toFormData(schema: DocSchema, doc: DocValues): FormData {
  return {
    sections: schema.sections.map((section) =>
      section.kind === 'fields'
        ? {
            type: 'fields' as const,
            ...(section.label ? { label: section.label } : {}),
            items: section.fields.map((field) => ({ key: field.key, label: field.label, value: doc.values[field.key] ?? '' })),
          }
        : {
            type: 'table' as const,
            key: section.key,
            label: section.label,
            columns: section.columns.map((column) => ({ key: column.key, label: column.label })),
            rows: (doc.tables[section.key] ?? []).filter((row) => !emptyRow(row)),
          },
    ),
  };
}
